import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Search, ShoppingCart, User, ClipboardCheck, LogOut, Menu as MenuIcon, ChevronRight } from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { getImageUrl, generateSlug } from '../../utils/helpers';
import menuService from '../../services/menuService'; 
import catalogService from '../../services/catalogService'; 

const Header = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const isHomePage = location.pathname === '/'; 
    const isMenuPage = location.pathname.startsWith('/menu') || location.pathname.startsWith('/danh-muc'); 

    const [menus, setMenus] = useState([]);
    const [isCategoryOpen, setIsCategoryOpen] = useState(false);
    const [searchKeyword, setSearchKeyword] = useState('');
    const categoryContainerClass = "absolute top-[calc(100%+12px)] left-0 w-[280px] z-[150] opacity-0 invisible group-hover/catbtn:opacity-100 group-hover/catbtn:visible transition-all duration-200 bg-gradient-to-r from-orange-100 to-orange-50 shadow-xl border border-orange-300 rounded-xl py-2";
    
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    const dropdownRef = useRef(null);

    const [user, setUser] = useState(null);
    const { cartCount } = useCart();
    const LogoImg = '/logo.png';
    const Logo2Img = '/logo2.png';

    useEffect(() => {
        const userStr = localStorage.getItem('user_info');
        if (userStr) {
            setUser(JSON.parse(userStr));
        }

        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const rawRole = user?.role?.roleName || user?.role || '';
    const role = rawRole.toUpperCase();
    const isAdminOrManager = user && (role === 'ROLE_ADMIN' || role === 'ADMIN' || role === 'ROLE_STAFF' || role === 'STAFF');
    const isJustStaff = role === 'ROLE_STAFF' || role === 'STAFF';
    const adminDashboardLink = isJustStaff ? '/admin/orders' : '/admin';

    const isDropdownVisible = !isMenuPage && ((!isMobile && isHomePage) || isCategoryOpen);

    useEffect(() => {
        if (isDropdownVisible && dropdownRef.current) {
            const height = dropdownRef.current.offsetHeight + 6;
            document.documentElement.style.setProperty('--menu-height', `${height}px`);
        } else {
            document.documentElement.style.setProperty('--menu-height', `0px`);
        }
    }, [isDropdownVisible, menus]);

    const handleLogout = () => {
        localStorage.removeItem('access_token');
        localStorage.removeItem('user_info');
        setUser(null); // Chỉ cần set lại state user là UI tự update
        navigate('/'); 
    };

    const handleSearch = (e) => {
        if (e.type === 'click' || e.key === 'Enter') {
            if (searchKeyword.trim()) {
                navigate(`/search?q=${encodeURIComponent(searchKeyword.trim())}`);
            }
        }
    };

    const [appConfig, setAppConfig] = useState({
        zalo_link: '',
        messenger_link: '',
        hotline: 'Đang cập nhật'
    });

    useEffect(() => {
        const fetchMenusAndConfig = async () => {
            try {
                const [menuRes, configRes] = await Promise.all([
                    menuService.getPublicMenus().catch(() => []),
                    catalogService.getSystemConfig().catch(() => ({}))
                ]);
                
                setMenus(menuRes?.data || menuRes || []);
                
                const confData = configRes?.data || configRes;
                if (confData && Object.keys(confData).length > 0) {
                    setAppConfig(prev => ({ ...prev, ...confData }));
                }
            } catch (error) { console.error("Lỗi:", error); }
        };
        fetchMenusAndConfig();
    }, []);

    const categoryMenus = menus.filter(m => m.menuPosition === 'CATEGORY').sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0));
    const headerMenus = menus.filter(m => m.menuPosition === 'HEADER' && m.isShownInMainPage === true).sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0));

    // NỘI DUNG MENU ĐỔ XUỐNG DÙNG CHUNG CHO MỌI TRẠNG THÁI
    const categoryListContent = (
        <ul className="flex flex-col relative py-2" ref={dropdownRef}>
            {categoryMenus.map((cat, idx) => (
                <li key={cat.menuId} className="group/item static border-b border-orange-100/60 last:border-0">
                    <Link 
                        to={`/danh-muc/${generateSlug(cat.menuName)}-id${cat.menuId}`} 
                        className="flex items-stretch text-[13px] font-bold transition-all duration-200 relative z-10 text-gray-800 group-hover/item:text-white group-hover/item:bg-tnc-orange rounded-xl mx-2"
                    >
                        <div className="flex items-center justify-center pl-4 pr-3 py-2.5 shrink-0">
                            {cat.menuIcon ? (
                                <img src={getImageUrl(cat.menuIcon)} alt="" className="w-5 h-5 object-contain transition-transform duration-300 group-hover/item:scale-110 group-hover/item:brightness-0 group-hover/item:invert" />
                            ) : (
                                <div className="w-5 h-5"></div>
                            )}
                        </div>
                        <div className="flex-1 flex items-center justify-between pr-4 pl-1 py-2.5 transition-all duration-300">
                            <span className="whitespace-normal break-words leading-snug transition-transform duration-300 origin-left group-hover/item:translate-x-1">
                                {cat.menuName}
                            </span>
                            {cat.subMenus && cat.subMenus.length > 0 && (
                                <ChevronRight size={16} className="shrink-0 ml-2 transition-colors text-gray-400 group-hover/item:text-white" />
                            )}
                        </div>
                    </Link>

                    {cat.subMenus && cat.subMenus.length > 0 && (
                        <div className="absolute top-0 left-full pl-1 hidden group-hover/item:block opacity-0 group-hover/item:opacity-100 transition-opacity duration-300 z-50 h-full">
                            <div className="absolute top-0 -left-4 w-6 h-[120%] bg-transparent z-[-1]"></div>
                            <div className="w-max min-w-[250px] max-w-[850px] min-h-[360px] max-h-[80vh] pr-8 bg-gradient-to-br from-orange-50 to-white shadow-xl border border-orange-200/60 rounded-xl p-6 overflow-y-auto custom-scrollbar">
                                <div className="flex flex-wrap gap-x-12 gap-y-8">
                                    {[...cat.subMenus].sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0)).map(sub => (
                                        <div key={sub.menuId} className="flex flex-col min-w-[160px] max-w-[280px]">
                                            <Link to={`/danh-muc/${generateSlug(sub.menuName)}-id${sub.menuId}`} className="flex items-center gap-2.5 font-bold text-tnc-orange text-sm mb-3 pb-2 border-b border-orange-200/60 leading-snug">
                                                {sub.menuIcon && (
                                                    <img src={getImageUrl(sub.menuIcon)} alt="" className="w-7 h-7 object-contain shrink-0" />
                                                )}
                                                <span className="whitespace-normal break-words uppercase">{sub.menuName}</span>
                                            </Link>
                                            {sub.subMenus && sub.subMenus.length > 0 && (
                                                <ul className="flex flex-col space-y-2">
                                                    {[...sub.subMenus].sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0)).map(sub3 => (
                                                        <li key={sub3.menuId} className="relative group/sub3">
                                                            <Link to={`/danh-muc/${generateSlug(sub3.menuName)}-id${sub3.menuId}`} className="group/link3 flex items-center justify-between text-[13px] font-bold text-gray-800 transition-all duration-200 py-1.5 w-max border-b-2 border-transparent hover:border-tnc-orange hover:text-tnc-orange">
                                                                <div className="flex items-center">
                                                                    {sub3.menuIcon ? (
                                                                        <img src={getImageUrl(sub3.menuIcon)} alt="" className="w-7 h-7 object-contain shrink-0 mr-2.5" />
                                                                    ) : (
                                                                        <span className="text-gray-300 group-hover/link3:text-tnc-orange mr-2 font-black text-[10px] shrink-0">•</span>
                                                                    )}
                                                                    <span className="whitespace-normal break-words leading-snug">{sub3.menuName}</span>
                                                                </div>
                                                                {sub3.subMenus && sub3.subMenus.length > 0 && (
                                                                    <ChevronRight size={14} className="ml-2 shrink-0 text-gray-400 group-hover/link3:text-tnc-orange" />
                                                                )}
                                                            </Link>
                                                            
                                                            {sub3.subMenus && sub3.subMenus.length > 0 && (
                                                                <div className="absolute top-0 left-full pl-4 hidden group-hover/sub3:block z-50">
                                                                    <div className="absolute top-0 -left-4 w-6 h-full bg-transparent z-[-1]"></div>
                                                                    <div className="w-max min-w-[200px] bg-gradient-to-br from-orange-50 to-white shadow-lg border border-orange-200/60 rounded-lg p-4">
                                                                        <ul className="flex flex-col space-y-2">
                                                                            {[...sub3.subMenus].sort((a, b) => (a.menuOrder || 0) - (b.menuOrder || 0)).map(sub4 => (
                                                                                <li key={sub4.menuId}>
                                                                                    <Link to={`/danh-muc/${generateSlug(sub4.menuName)}-id${sub4.menuId}`} className="group/link4 flex items-center text-[13px] font-bold text-gray-800 transition-all duration-200 py-1.5 w-max border-b-2 border-transparent hover:border-tnc-orange hover:text-tnc-orange">
                                                                                        {sub4.menuIcon ? (
                                                                                            <img src={getImageUrl(sub4.menuIcon)} alt="" className="w-4 h-4 object-contain shrink-0 mr-2.5" />
                                                                                        ) : (
                                                                                            <span className="text-gray-300 group-hover/link4:text-tnc-orange mr-2 font-black text-[10px] shrink-0">•</span>
                                                                                        )}
                                                                                        <span className="whitespace-normal break-words leading-snug">{sub4.menuName}</span>
                                                                                    </Link>
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </li>
            ))}
        </ul>
    );

    // CHUNG: Khối giao diện Thanh Tìm Kiếm
    const searchBarContent = (
        <div className="relative flex items-center w-full h-11 lg:h-[44px] rounded-full bg-white overflow-hidden border border-orange-200/60 focus-within:ring-2 focus-within:ring-tnc-orange/30 focus-within:border-tnc-orange transition-all shadow-sm">
            <input 
                type="text" 
                placeholder="Bạn cần tìm linh kiện, PC hay Laptop..." 
                className="w-full h-full bg-transparent pl-5 pr-14 text-sm lg:text-[15px] outline-none text-gray-800 placeholder-gray-500 font-medium" 
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyDown={handleSearch}
            />
            <button 
                onClick={handleSearch}
                className="absolute right-1 top-1 bottom-1 w-10 flex items-center justify-center bg-tnc-orange text-white hover:bg-tnc-orange-hover transition-colors rounded-full shadow-sm"
            >
                <Search size={18} strokeWidth={3} />
            </button>
        </div>
    );

    const showLargeLogo = (isHomePage || isMenuPage) && !isMobile;
    const currentLogo = showLargeLogo ? LogoImg : Logo2Img;
    const showCatBtn = !showLargeLogo;

    return (
        <div className="w-full z-50 relative">
            <div className="bg-gradient-to-r from-tnc-orange-dark via-tnc-orange to-tnc-orange-light shadow-md relative z-[110]">
                
                {/* CONTAINER CHÍNH */}
                <div className="container mx-auto px-4 pt-3 pb-1 md:pt-4 md:pb-2 flex items-stretch gap-4 lg:gap-6 relative z-[120]">
                    
                    {/* CỘT TRÁI: DANH MỤC & LOGO */}
                    <div className="flex items-center justify-center gap-3 lg:gap-4 shrink-0 w-auto lg:w-[260px] xl:w-[280px] h-full min-h-[50px] lg:min-h-[85px] relative z-50">
                        
                        {/* Nút Danh Mục */}
                        {showCatBtn && (
                            <div className="relative group/catbtn h-full flex items-center"
                                onMouseEnter={() => { if (!isMobile) setIsCategoryOpen(true) }}
                                onMouseLeave={() => { if (!isMobile) setIsCategoryOpen(false) }}
                            >
                                <button 
                                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                                    className="bg-tnc-orange text-white font-sans flex items-center justify-center gap-2 h-11 lg:h-[44px] px-3 lg:px-4 rounded-xl font-medium transition-transform hover:scale-105 hover:bg-tnc-orange-hover shadow-md shrink-0 border border-white/20"
                                >
                                    <MenuIcon size={22} />
                                    <span className="hidden lg:block text-[14px] uppercase tracking-wide whitespace-nowrap mt-0.5">
                                        Danh mục
                                    </span>
                                </button>
                                
                                {/* ÁP DỤNG BIẾN Ở ĐÂY */}
                                <div className={categoryContainerClass}>
                                    {categoryListContent}
                                </div>
                            </div>
                        )}

                        <div className="flex items-center justify-center h-[64px] shrink-0">
                            <Link to="/" className="flex items-center justify-center h-full">
                                <img 
                                    src={currentLogo} 
                                    alt="Thành Nam PC" 
                                    width="140" 
                                    height="64"
                                    className="h-full w-auto object-contain" 
                                    loading="eager" // Logo là thành phần quan trọng, load ngay lập tức
                                />
                            </Link>
                        </div>
                    </div>

                    {/* CỘT PHẢI: CHỨA HÀNG SEARCH & HÀNG MENU HEADER/HOTLINE */}
                    <div className="flex-1 flex flex-col justify-between py-0.5 lg:py-0.5">
                        
                        {/* Hàng 1: Search Bar & Giỏ hàng / Tài khoản */}
                        <div className="flex items-center w-full justify-between gap-4 lg:gap-8">
                            {/* Search (Desktop) */}
                            <div className="flex-1 hidden lg:block max-w-[700px]">
                                {searchBarContent}
                            </div>

                            {/* User & Cart */}
                            <div className="flex items-center gap-5 lg:gap-8 shrink-0 ml-auto">
                                {user && (
                                    <Link to="/cart" className="flex items-center gap-2 hover:opacity-80 transition-opacity group relative">
                                        <div className="p-2 md:p-2.5 bg-white/20 rounded-full group-hover:bg-white/30 transition-colors backdrop-blur-sm border border-white/30">
                                            <ShoppingCart size={isMobile ? 20 : 22} className="text-white" />
                                        </div>
                                        <span className="absolute -top-1 -right-1 bg-white text-tnc-orange text-[10px] font-extrabold w-5 h-5 rounded-full flex items-center justify-center shadow-md">
                                            {cartCount}
                                        </span>
                                        <span className="text-sm font-bold text-white hidden xl:block">Giỏ hàng</span>
                                    </Link>
                                )}
                                
                                {user ? (
                                    <div className="relative group cursor-pointer flex items-center gap-3">
                                        <div className="w-10 h-10 md:w-11 md:h-11 bg-white text-tnc-orange rounded-full flex items-center justify-center font-extrabold shadow-md border-2 border-white/50 backdrop-blur-sm relative z-[99999]">
                                            {user.username.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="hidden lg:block text-white">
                                            <span className="text-xs text-white/80 block leading-none">Xin chào,</span>
                                            <span className="text-sm font-bold block leading-none mt-1 truncate max-w-[150px]">{user.accountName || user.username}</span>
                                        </div>
                                        
                                        <div className="absolute top-full right-0 mt-3 w-56 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.3)] border border-orange-200/60 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right scale-95 group-hover:scale-100 z-[400]">
                                            <div className="absolute -top-4 left-0 w-full h-4 bg-transparent"></div>
                                            <div className="p-2 relative z-50">
                                                {isAdminOrManager && (
                                                    <Link 
                                                        to={adminDashboardLink} 
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="flex items-center px-4 py-2.5 text-sm font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg mb-1 transition-colors"
                                                    >
                                                        <ClipboardCheck size={18} className="mr-2"/> Trang Quản Trị
                                                    </Link>
                                                )}
                                                <Link to="/profile" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-tnc-orange rounded-lg transition-colors"><User size={18} className="mr-2"/> Tài khoản của tôi</Link>
                                                <Link to="/my-orders" className="flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-tnc-orange rounded-lg transition-colors"><ShoppingCart size={18} className="mr-2"/> Đơn hàng của tôi</Link>
                                            </div>
                                            <div className="border-t border-orange-100 p-2 relative z-50">
                                                <button onClick={handleLogout} className="flex items-center w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 font-bold rounded-lg transition-colors"><LogOut size={18} className="mr-2"/> Đăng xuất</button>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-3">
                                        <Link to="/login" className="hidden lg:flex items-center justify-center border-2 border-white/80 text-white hover:bg-white/20 px-6 py-2.5 text-sm font-bold rounded-full transition-all active:scale-95 backdrop-blur-sm">
                                            Đăng nhập
                                        </Link>
                                        <Link to="/register" className="hidden lg:flex items-center justify-center bg-white text-tnc-orange hover:bg-gray-100 px-6 py-2.5 text-sm font-extrabold rounded-full transition-all shadow-md active:scale-95">
                                            Đăng ký
                                        </Link>
                                        <Link to="/login" className="lg:hidden p-2 bg-white/20 border border-white/30 backdrop-blur-sm rounded-full text-white hover:bg-white/30 transition-colors">
                                            <User size={20} />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Hàng 2: Menu Header & Hotline (Desktop) */}
                        <div className="hidden lg:flex items-center justify-between w-full mt-auto pt-3">
                            <nav className="flex-1 flex items-center h-full mr-6 pr-4">
                                <div className="flex flex-wrap w-full justify-start items-center gap-1 xl:gap-6 pt-1">
                                    {headerMenus.map((menu) => (
                                        <Link key={menu.menuId} to={menu.menuUrl || '#'} title={menu.menuName} className="flex-1 flex justify-center items-center gap-1 text-white hover:bg-white/20 transition-all duration-300 font-bold tracking-wide uppercase py-2 px-2 text-[12px] xl:text-[14px] rounded-xl active:scale-95 text-center">
                                            {menu.menuIcon && (
                                                <img src={getImageUrl(menu.menuIcon)} alt="" className="w-4 h-4 xl:w-5 xl:h-5 object-contain shrink-0" />
                                            )}
                                            <span className="truncate">{menu.menuName}</span>
                                        </Link>
                                    ))}
                                </div>
                            </nav>
                            
                            <div className="font-medium text-sm text-white items-center gap-2 hidden xl:flex shrink-0 bg-white/20 px-5 py-1.5 rounded-full border border-white/40 backdrop-blur-sm shadow-sm ml-auto pt-1">
                                Hotline: <span className="font-extrabold text-base tracking-wide text-white">{appConfig.hotline || '0999 999 999'}</span>
                            </div>
                        </div>

                    </div>
                </div>

                <div className="block lg:hidden w-full px-4 pb-3 relative z-[115]">
                    {searchBarContent}
                </div>
            </div>

            {/* ===== KHỐI NÚT LIÊN HỆ GÓC MÀN HÌNH MỚI ===== */}
            <div className="fixed bottom-[60px] md:bottom-[65px] right-4 md:right-6 flex flex-col items-end gap-3 z-40">
                {/* Nút Zalo */}
                <a 
                    href={appConfig.zalo_link ? appConfig.zalo_link : "#"} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center justify-center gap-0 md:gap-2.5 bg-[#0068FF] text-white w-12 h-12 md:w-auto md:h-auto md:px-4 md:py-2.5 rounded-full shadow-[0_4px_15px_rgba(0,104,255,0.4)] hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                    {/* Icon Mobile */}
                    <span className="md:hidden font-bold text-xs tracking-wide">Zalo</span>
                    
                    {/* Nội dung Desktop */}
                    <span className="hidden md:inline-block bg-white text-[#0068FF] font-black text-[10px] md:text-xs rounded px-1.5 py-0.5 shadow-sm">Zalo</span>
                    <span className="hidden md:inline-block font-bold text-[12px] md:text-[13px] tracking-wide whitespace-nowrap">Liên hệ qua Zalo</span>
                </a>
                
                {/* Nút Messenger */}
                <a 
                    href={appConfig.messenger_link ? appConfig.messenger_link : "#"} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="flex items-center justify-center gap-0 md:gap-2.5 bg-[#0866FF] text-white w-12 h-12 md:w-auto md:h-auto md:px-4 md:py-2.5 rounded-full shadow-[0_4px_15px_rgba(8,102,255,0.4)] hover:-translate-y-1 hover:shadow-lg transition-all"
                >
                    <svg fill="currentColor" viewBox="0 0 24 24" className="w-6 h-6 shrink-0"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.469h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.469h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                    
                    {/* Chữ chỉ hiện ở Desktop */}
                    <span className="hidden md:inline-block font-bold text-[12px] md:text-[13px] tracking-wide whitespace-nowrap">Liên hệ Messenger</span>
                </a>
            </div>
        </div>
    );
};

export default Header;